import { formatDuration, humanDuration } from './duration';
import { REFERENCES } from './links';
import type { ResolvedPlan, ResolvedTier } from './resolve';
import type { DesignConfig } from './types';

export interface GeneratedTask {
  /** Task name, also used as the download filename stem. */
  name: string;
  /** Destination bucket, used as the panel heading. */
  bucket: string;
  /** Suggested filename, e.g. `downsample_sensor_reading_to_telemetry_1m.flux`. */
  filename: string;
  /** The complete, copy-pasteable Flux script. */
  script: string;
}

/** Flux keeps `mean`, `min`, … in scope as builtins, so pipeline vars get a prefix. */
const VAR_PREFIX = 'agg_';

function quoteList(values: string[]): string {
  return values.map((v) => JSON.stringify(v)).join(', ');
}

/** Build the `_field` predicate. A single field gets a plain `==` for readability. */
function fieldFilter(fields: string[]): string | null {
  if (fields.length === 0) return null;
  const clauses = fields.map((f) => `r._field == ${JSON.stringify(f)}`);
  return clauses.join(' or ');
}

function taskOption(tier: ResolvedTier, config: DesignConfig): string {
  const lines = [`    name: ${JSON.stringify(tier.taskName)},`];
  const cron = config.schedule.cron.trim();
  if (cron !== '') {
    lines.push(`    cron: ${JSON.stringify(cron)},`);
  } else {
    lines.push(`    every: ${formatDuration(tier.everySeconds)},`);
  }
  if (tier.offsetSeconds > 0) {
    lines.push(`    offset: ${formatDuration(tier.offsetSeconds)},`);
  }
  return `option task = {\n${lines.join('\n')}\n}`;
}

function header(tier: ResolvedTier, plan: ResolvedPlan): string {
  const origin = tier.fromRaw ? 'raw' : 'rolled-up';
  const kept = Number.isFinite(tier.retentionSeconds)
    ? `kept for ${humanDuration(tier.retentionSeconds)}`
    : 'kept indefinitely';
  return [
    `// ${tier.taskName}`,
    `//`,
    `// Rolls up ${origin} ${plan.source.measurement} from "${tier.sourceBucket}"`,
    `// (${humanDuration(tier.sourceResolutionSeconds)} resolution) into "${tier.bucket}"`,
    `// at ${humanDuration(tier.windowSeconds)} resolution, ${kept}.`,
    `// Aggregates: ${tier.aggregates.join(', ')}.`,
    `//`,
    `// Background on the window-alignment and idempotency pattern used below:`,
    `// ${REFERENCES.robustRollups.url}`,
  ].join('\n');
}

function boundsBlock(tier: ResolvedTier): string {
  const window = formatDuration(tier.windowSeconds);
  const lookback = formatDuration(tier.lookbackSeconds);
  return [
    `// Truncating \`stop\` to the ${window} window means the task only ever writes whole`,
    `// windows — a window that is still filling is never written and then rewritten with`,
    `// a different value. The ${lookback} lookback re-reads recently closed windows so late`,
    `// arrivals are folded in; rewriting a window with \`to()\` is idempotent.`,
    `stop = date.truncate(t: now(), unit: ${window})`,
    `start = date.sub(d: ${lookback}, from: stop)`,
  ].join('\n');
}

function sourceBlock(tier: ResolvedTier, plan: ResolvedPlan, config: DesignConfig): string {
  const lines = [
    `source =`,
    `    from(bucket: ${JSON.stringify(tier.sourceBucket)})`,
    `        |> range(start: start, stop: stop)`,
    `        |> filter(fn: (r) => r._measurement == ${JSON.stringify(plan.source.measurement)})`,
  ];
  const fields = fieldFilter(plan.source.fields);
  if (fields) {
    lines.push(`        |> filter(fn: (r) => ${fields})`);
  }
  if (tier.fromRaw) {
    const dropped = config.source.tagKeys
      .filter((t) => !t.retained && t.key.trim() !== '')
      .map((t) => t.key);
    if (dropped.length > 0) {
      lines.push(`        // Dropped at the first hop: these tags multiply rollup cardinality.`);
      lines.push(`        |> drop(columns: [${quoteList(dropped)}])`);
    }
  }
  return lines.join('\n');
}

function aggregateBlocks(tier: ResolvedTier): string {
  const window = formatDuration(tier.windowSeconds);
  const createEmpty = tier.createEmpty ? 'true' : 'false';
  if (tier.aggregates.length === 1) {
    return [
      `source`,
      `    |> aggregateWindow(every: ${window}, fn: ${tier.aggregates[0]}, createEmpty: ${createEmpty})`,
    ].join('\n');
  }
  // Several aggregates: tag each stream so the rollup bucket can tell them apart,
  // then union into a single write.
  const blocks = tier.aggregates.map((agg) =>
    [
      `${VAR_PREFIX}${agg} =`,
      `    source`,
      `        |> aggregateWindow(every: ${window}, fn: ${agg}, createEmpty: ${createEmpty})`,
      `        |> set(key: "agg", value: ${JSON.stringify(agg)})`,
    ].join('\n'),
  );
  const names = tier.aggregates.map((a) => `${VAR_PREFIX}${a}`).join(', ');
  return `${blocks.join('\n\n')}\n\nunion(tables: [${names}])\n    |> sort(columns: ["_time"])`;
}

/** Generate the complete Flux task script for one tier. */
export function generateTierScript(
  tier: ResolvedTier,
  plan: ResolvedPlan,
  config: DesignConfig,
): string {
  return [
    header(tier, plan),
    '',
    'import "date"',
    '',
    taskOption(tier, config),
    '',
    boundsBlock(tier),
    '',
    sourceBlock(tier, plan, config),
    '',
    aggregateBlocks(tier),
    `    |> to(bucket: ${JSON.stringify(tier.bucket)}, org: ${JSON.stringify(plan.source.org)})`,
    '',
  ].join('\n');
}

/** Generate one task per tier, in cascade order. */
export function generateTasks(config: DesignConfig, plan: ResolvedPlan): GeneratedTask[] {
  return plan.tiers.map((tier) => ({
    name: tier.taskName,
    bucket: tier.bucket,
    filename: `${tier.taskName}.flux`,
    script: generateTierScript(tier, plan, config),
  }));
}

/**
 * Concatenate every task into one file for the "download all" action, separated by
 * banner comments so the result is still readable as a single document.
 */
export function bundleTasks(tasks: GeneratedTask[]): string {
  return tasks
    .map(
      (task) => `// ${'='.repeat(74)}\n// ${task.filename}\n// ${'='.repeat(74)}\n\n${task.script}`,
    )
    .join('\n');
}
